const FilterIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={`${className} shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
);

const SearchIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={`${className} shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

/**
 * Reusable filter bar: Active (all/active/inactive) + Search, with optional actions slot.
 * Use for list pages that filter by active state and search.
 */
const ActiveSearchFilters = ({
  activeState,
  onActiveChange,
  search,
  onSearchChange,
  showActiveFilter = true,
  showSearchFilter = true,
  searchPlaceholder = 'Search…',
  activeSelectId = 'filter-active',
  searchInputId = 'filter-search',
  onFilterChange,
  leadingFilters,
  /** Smaller labels/controls to match dense tables (e.g. My Diary activity plan). */
  compact = false,
  children,
}) => {
  const handleActiveChange = (e) => {
    const value = e.target.value;
    onActiveChange(value);
    onFilterChange?.();
  };

  const handleSearchChange = (e) => {
    onSearchChange(e.target.value);
    onFilterChange?.();
  };

  const labelClass = compact
    ? 'flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-dark'
    : 'flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-primary-dark';

  const selectClass = compact
    ? 'mt-0.5 h-7 w-[9.5rem] rounded-md border border-primary-dark/20 bg-gray-50 px-2 py-1 text-[10px] leading-tight text-slate-700 outline-none transition focus:border-primary-dark focus:ring-1 focus:ring-primary-dark/30'
    : 'mt-1 w-40 rounded-lg border border-primary-dark/20 bg-gray-50 px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-primary-dark focus:ring-1 focus:ring-primary-dark/30';

  const searchInputClass = compact
    ? 'w-full rounded-md border border-primary-dark/20 bg-gray-50 py-1.5 pl-8 pr-2 text-[10px] leading-tight text-slate-700 placeholder:text-slate-400 placeholder:text-[10px] outline-none transition focus:border-primary-dark focus:ring-1 focus:ring-primary-dark/30 sm:w-56'
    : 'w-full rounded-lg border border-primary-dark/20 bg-gray-50 py-2 pl-9 pr-3 text-sm text-slate-700 placeholder-slate-400 outline-none transition focus:border-primary-dark focus:ring-1 focus:ring-primary-dark/30 sm:w-64';

  const iconSize = compact ? 'h-3 w-3' : 'h-4 w-4';
  const searchIconOffset = compact ? 'left-2' : 'left-3';

  return (
    <div className={`flex flex-wrap items-end justify-between ${compact ? 'gap-2' : 'gap-3'}`}>
      <div className={`flex flex-col sm:flex-row sm:items-end ${compact ? 'gap-2' : 'gap-3'}`}>
        {leadingFilters ? <div className={showActiveFilter || showSearchFilter ? 'sm:mr-3' : ''}>{leadingFilters}</div> : null}
        {showActiveFilter ? (
          <div>
            <label htmlFor={activeSelectId} className={labelClass}>
              <FilterIcon className={iconSize} />
              Active
            </label>
            <select
              id={activeSelectId}
              value={activeState}
              onChange={handleActiveChange}
              className={selectClass}
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        ) : null}

        {showSearchFilter ? (
          <div className={showActiveFilter ? 'sm:ml-3' : ''}>
            <label htmlFor={searchInputId} className={labelClass}>
              <SearchIcon className={iconSize} />
              Search
            </label>
            <div className={compact ? 'relative mt-0.5' : 'relative mt-1'}>
              <span className={`pointer-events-none absolute inset-y-0 flex items-center text-slate-400 ${searchIconOffset}`}>
                <SearchIcon className={iconSize} />
              </span>
              <input
                id={searchInputId}
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                autoComplete="off"
                className={searchInputClass}
              />
            </div>
          </div>
        ) : null}
      </div>

      {children}
    </div>
  );
};

export default ActiveSearchFilters;
